/**
 * AI Agents Management Page
 *
 * Enterprise features:
 * - Real-time agent stats with polling
 * - Agent CRUD operations
 * - Start/Pause/Stop controls
 * - Efficiency tracking
 * - Agent logs viewer
 */

import { useState, useMemo, useCallback } from 'react';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import Modal from '../../components/modals/Modal';
import { agentsApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useQuery, useMutation } from '../../hooks/useDataFetch';
import './LazyMarketing.css';

// Constants
const STATS_REFRESH_INTERVAL = 15000; // 15 seconds - agents need fast updates

const Agents = () => {
  const { showToast } = useToast();
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAgent, setNewAgent] = useState({
    name: '',
    type: 'content-writer',
    description: '',
  });

  // Fetch agents with stats
  const {
    data: agentsData,
    isLoading: loadingAgents,
    refetch: refetchAgents,
  } = useQuery(
    () => agentsApi.list({
      status: statusFilter === 'all' ? undefined : statusFilter,
      includeStats: true,
    }),
    {
      key: `agents:list:${statusFilter}`,
      cacheTTL: 10000,
      refetchInterval: STATS_REFRESH_INTERVAL,
    }
  );

  // Fetch global agent stats
  const {
    data: globalStats,
    isLoading: loadingStats,
  } = useQuery(
    () => agentsApi.stats(),
    {
      key: 'agents:globalStats',
      cacheTTL: 15000,
      refetchInterval: STATS_REFRESH_INTERVAL,
    }
  );

  // Create agent mutation
  const createMutation = useMutation(
    (data) => agentsApi.create(data),
    {
      onSuccess: () => {
        showToast('Agent deployed', 'success');
        refetchAgents();
        setShowCreateModal(false);
        setNewAgent({ name: '', type: 'content-writer', description: '' });
      },
      onError: (error) => {
        showToast(error.message || 'Failed to deploy agent', 'error');
      },
    }
  );

  // Start/Pause/Stop mutations
  const startMutation = useMutation(
    (id) => agentsApi.start(id),
    {
      onSuccess: () => {
        showToast('Agent started', 'success');
        refetchAgents();
      },
      onError: (error) => {
        showToast(error.message || 'Failed to start agent', 'error');
      },
    }
  );

  const pauseMutation = useMutation(
    (id) => agentsApi.pause(id),
    {
      onSuccess: () => {
        showToast('Agent paused', 'success');
        refetchAgents();
      },
      onError: (error) => {
        showToast(error.message || 'Failed to pause agent', 'error');
      },
    }
  );

  const stopMutation = useMutation(
    (id) => agentsApi.stop(id),
    {
      onSuccess: () => {
        showToast('Agent stopped', 'success');
        refetchAgents();
      },
      onError: (error) => {
        showToast(error.message || 'Failed to stop agent', 'error');
      },
    }
  );

  // Compute stats
  const stats = useMemo(() => {
    const data = globalStats || {};
    const agentList = agentsData?.agents || agentsData?.data || [];

    const activeAgents = agentList.filter(a => a.status === 'Active' || a.status === 'active').length;
    const totalTasks = agentList.reduce((sum, a) => sum + (a.tasksCompleted || a.tasks || 0), 0);
    const avgEfficiency = agentList.length > 0
      ? Math.round(agentList.reduce((sum, a) => sum + (parseFloat(a.efficiency) || 0), 0) / agentList.length)
      : 0;
    const costSaved = agentList.reduce((sum, a) => sum + (a.costSaved || 0), 0);

    return [
      {
        label: 'Active Agents',
        value: data.activeAgents || activeAgents,
        change: formatChange(data.activeChange),
        changeType: getChangeType(data.activeChange),
        icon: 'cpu',
      },
      {
        label: 'Tasks Completed',
        value: formatNumber(data.tasksCompleted || totalTasks),
        change: formatChange(data.tasksChange),
        changeType: getChangeType(data.tasksChange),
        icon: 'check-square',
      },
      {
        label: 'Efficiency',
        value: `${data.avgEfficiency || avgEfficiency}%`,
        change: formatChange(data.efficiencyChange, '%'),
        changeType: getChangeType(data.efficiencyChange),
        icon: 'zap',
      },
      {
        label: 'Cost Saved',
        value: formatCurrency(data.costSaved || costSaved),
        change: formatChange(data.costChange),
        changeType: getChangeType(data.costChange),
        icon: 'dollar-sign',
      },
    ];
  }, [globalStats, agentsData]);

  // Extract agents list
  const agentsList = useMemo(() => {
    const data = agentsData || {};
    return data.agents || data.data || [];
  }, [agentsData]);

  // Handlers
  const handleRefresh = useCallback(() => {
    refetchAgents();
    showToast('Agents refreshed', 'success');
  }, [refetchAgents, showToast]);

  const handleAgentClick = useCallback((agent) => {
    setSelectedAgent(agent);
    setShowDetailModal(true);
  }, []);

  const handleStatusAction = useCallback((agentId, action) => {
    switch (action) {
      case 'start':
        startMutation.mutate(agentId);
        break;
      case 'pause':
        pauseMutation.mutate(agentId);
        break;
      case 'stop':
        stopMutation.mutate(agentId);
        break;
      default:
        break;
    }
  }, [startMutation, pauseMutation, stopMutation]);

  const handleCreateAgent = useCallback(() => {
    if (!newAgent.name) {
      showToast('Please enter an agent name', 'error');
      return;
    }
    createMutation.mutate({
      name: newAgent.name,
      type: newAgent.type,
      description: newAgent.description,
      status: 'inactive',
    });
  }, [newAgent, createMutation, showToast]);

  // Helpers
  function formatCurrency(amount) {
    if (!amount) return '$0';
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(amount >= 10000 ? 0 : 1)}K`;
    return `$${amount.toLocaleString()}`;
  }

  function formatNumber(num) {
    if (!num) return '0';
    return num.toLocaleString();
  }

  function formatChange(value, suffix = '') {
    if (value === null || value === undefined) return undefined;
    return `${value >= 0 ? '+' : ''}${value}${suffix}`;
  }

  function getChangeType(value) {
    if (value === null || value === undefined) return 'neutral';
    return value >= 0 ? 'positive' : 'negative';
  }

  function getStatusStyle(status) {
    switch (status?.toLowerCase()) {
      case 'active':
        return { background: 'var(--success-soft)', color: 'var(--success)' };
      case 'paused':
        return { background: 'var(--warning-soft)', color: 'var(--warning)' };
      case 'inactive':
      case 'stopped':
        return { background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)' };
      case 'error':
        return { background: 'var(--danger-soft)', color: 'var(--danger)' };
      default:
        return { background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)' };
    }
  }

  function getEfficiencyColor(efficiency) {
    const value = parseFloat(efficiency) || 0;
    if (value >= 90) return 'var(--success)';
    if (value >= 70) return 'var(--warning)';
    return 'var(--danger)';
  }

  function getAgentIcon(type) {
    switch (type?.toLowerCase()) {
      case 'content-writer':
        return '✍️';
      case 'social-manager':
        return '📱';
      case 'email-composer':
        return '📧';
      case 'ad-optimizer':
        return '📊';
      case 'lead-qualifier':
        return '🎯';
      case 'analytics-bot':
        return '📈';
      default:
        return '🤖';
    }
  }

  const isLoading = loadingAgents && agentsList.length === 0;

  return (
    <div className="lm-page">
      {/* Header */}
      <div className="lm-page-header">
        <div>
          <h1 className="lm-page-title">AI Agents</h1>
          <p className="lm-page-subtitle">Manage your automated marketing agents</p>
        </div>
        <div className="lm-page-actions">
          <Button variant="secondary" icon="refresh" onClick={handleRefresh} disabled={loadingAgents}>
            Refresh
          </Button>
          <Button variant="primary" icon="plus" onClick={() => setShowCreateModal(true)}>
            Deploy Agent
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="lm-stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} loading={loadingStats && !globalStats} />
        ))}
      </div>

      {/* Filters */}
      <div className="lm-filters">
        <div className="filter-tabs">
          {['all', 'active', 'paused', 'inactive'].map((filter) => (
            <button
              key={filter}
              className={`filter-tab ${statusFilter === filter ? 'active' : ''}`}
              onClick={() => setStatusFilter(filter)}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Agents Grid */}
      <div className="lm-section">
        <div className="lm-section-header">
          <h3 className="lm-section-title">Agent Status</h3>
          <span className="lm-section-count">
            {loadingAgents ? '...' : `${agentsList.length} agents`}
          </span>
        </div>

        {isLoading ? (
          <div className="lm-grid">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="lm-grid-card skeleton">
                <div className="skeleton-header">
                  <div className="skeleton-line" style={{ width: '60%' }}></div>
                  <div className="skeleton-badge"></div>
                </div>
                <div className="skeleton-content">
                  <div className="skeleton-line" style={{ width: '80%' }}></div>
                </div>
              </div>
            ))}
          </div>
        ) : agentsList.length > 0 ? (
          <div className="lm-grid">
            {agentsList.map((agent) => (
              <div
                key={agent.id}
                className="lm-grid-card clickable"
                onClick={() => handleAgentClick(agent)}
              >
                <div className="lm-grid-card-header">
                  <div className="agent-title">
                    <span className="agent-icon">{getAgentIcon(agent.type)}</span>
                    <span className="lm-grid-card-title">{agent.name}</span>
                  </div>
                  <span className="lm-grid-card-badge" style={getStatusStyle(agent.status)}>
                    {agent.status}
                  </span>
                </div>
                <div className="lm-grid-card-content">
                  <div className="agent-metrics">
                    <div className="agent-metric">
                      <span className="metric-label">Tasks</span>
                      <span className="metric-value">{formatNumber(agent.tasksCompleted || agent.tasks)}</span>
                    </div>
                    <div className="agent-metric">
                      <span className="metric-label">Efficiency</span>
                      <span className="metric-value" style={{ color: getEfficiencyColor(agent.efficiency) }}>
                        {agent.efficiency || '0'}%
                      </span>
                    </div>
                  </div>
                  <div className="efficiency-bar">
                    <div
                      className="efficiency-fill"
                      style={{
                        width: `${agent.efficiency || 0}%`,
                        background: getEfficiencyColor(agent.efficiency),
                      }}
                    ></div>
                  </div>
                </div>
                <div className="lm-grid-card-footer">
                  <span className="agent-type">{agent.type || 'General'}</span>
                  <div className="agent-actions">
                    {agent.status?.toLowerCase() === 'active' ? (
                      <button
                        className="action-btn pause"
                        onClick={(e) => { e.stopPropagation(); handleStatusAction(agent.id, 'pause'); }}
                        title="Pause"
                      >
                        ⏸
                      </button>
                    ) : agent.status?.toLowerCase() !== 'stopped' ? (
                      <button
                        className="action-btn start"
                        onClick={(e) => { e.stopPropagation(); handleStatusAction(agent.id, 'start'); }}
                        title="Start"
                      >
                        ▶
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="lm-empty">
            <Icon name="cpu" size="xl" />
            <p>No agents deployed</p>
            <Button variant="primary" icon="plus" onClick={() => setShowCreateModal(true)}>
              Deploy your first agent
            </Button>
          </div>
        )}
      </div>

      {/* Create Agent Modal */}
      {showCreateModal && (
        <Modal
          title="Deploy New Agent"
          onClose={() => setShowCreateModal(false)}
        >
          <div className="create-agent-form">
            <div className="form-group">
              <label className="form-label">Agent Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter agent name"
                value={newAgent.name}
                onChange={(e) => setNewAgent(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Agent Type</label>
              <select
                className="form-select"
                value={newAgent.type}
                onChange={(e) => setNewAgent(prev => ({ ...prev, type: e.target.value }))}
              >
                <option value="content-writer">Content Writer</option>
                <option value="social-manager">Social Manager</option>
                <option value="email-composer">Email Composer</option>
                <option value="ad-optimizer">Ad Optimizer</option>
                <option value="lead-qualifier">Lead Qualifier</option>
                <option value="analytics-bot">Analytics Bot</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Description (optional)</label>
              <textarea
                className="form-textarea"
                placeholder="Describe what this agent should do"
                value={newAgent.description}
                onChange={(e) => setNewAgent(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateAgent}
                disabled={createMutation.isLoading}
              >
                {createMutation.isLoading ? 'Deploying...' : 'Deploy Agent'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Agent Detail Modal */}
      {showDetailModal && selectedAgent && (
        <Modal
          title="Agent Details"
          onClose={() => setShowDetailModal(false)}
        >
          <div className="agent-detail-modal">
            <div className="agent-detail-header">
              <div className="agent-icon-large">{getAgentIcon(selectedAgent.type)}</div>
              <div className="agent-info">
                <h3>{selectedAgent.name}</h3>
                <span className="agent-type-label">{selectedAgent.type}</span>
              </div>
              <span className="agent-status" style={getStatusStyle(selectedAgent.status)}>
                {selectedAgent.status}
              </span>
            </div>

            <div className="agent-detail-stats">
              <div className="detail-stat">
                <span className="stat-value">{formatNumber(selectedAgent.tasksCompleted || selectedAgent.tasks)}</span>
                <span className="stat-label">Tasks Completed</span>
              </div>
              <div className="detail-stat">
                <span className="stat-value" style={{ color: getEfficiencyColor(selectedAgent.efficiency) }}>
                  {selectedAgent.efficiency || '0'}%
                </span>
                <span className="stat-label">Efficiency</span>
              </div>
              <div className="detail-stat">
                <span className="stat-value">{formatCurrency(selectedAgent.costSaved)}</span>
                <span className="stat-label">Cost Saved</span>
              </div>
              <div className="detail-stat">
                <span className="stat-value">{selectedAgent.uptime || '0'}h</span>
                <span className="stat-label">Uptime</span>
              </div>
            </div>

            <div className="agent-detail-section">
              <h4>Description</h4>
              <p>{selectedAgent.description || 'No description provided.'}</p>
            </div>

            <div className="agent-detail-section">
              <h4>Recent Activity</h4>
              <div className="agent-activity-log">
                {selectedAgent.recentActivity?.length > 0 ? (
                  selectedAgent.recentActivity.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <span className="activity-time">{activity.time}</span>
                      <span className="activity-message">{activity.message}</span>
                    </div>
                  ))
                ) : (
                  <p className="no-activity">No recent activity</p>
                )}
              </div>
            </div>

            <div className="agent-detail-actions">
              <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
              {selectedAgent.status?.toLowerCase() === 'active' ? (
                <Button
                  variant="warning"
                  onClick={() => handleStatusAction(selectedAgent.id, 'pause')}
                  disabled={pauseMutation.isLoading}
                >
                  Pause Agent
                </Button>
              ) : selectedAgent.status?.toLowerCase() !== 'stopped' ? (
                <Button
                  variant="primary"
                  onClick={() => handleStatusAction(selectedAgent.id, 'start')}
                  disabled={startMutation.isLoading}
                >
                  Start Agent
                </Button>
              ) : null}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Agents;
